import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Building2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const EMPTY = { code: "", name: "", gstin: "", state: "", address: "" };

export default function Companies() {
  const { isOrgAdmin } = useAuth();
  const { refresh: refreshCompanyCtx } = useCompany() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/companies");
      setCompanies(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (company) => {
    try {
      await api.patch(`/masters/companies/${company.id}`, { is_active: !company.is_active });
      load();
      refreshCompanyCtx?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <PageHeader
        title="Companies"
        description="Legal entities under your organisation (e.g. SA Construction, SARPL)."
        action={isOrgAdmin && <CreateCompanyDialog onCreated={() => { load(); refreshCompanyCtx?.(); }} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && companies.length === 0 ? (
          <EmptyState title="No companies yet" description="Create your first legal entity to get started." testId="companies-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>GSTIN</TableHead><TableHead>State</TableHead><TableHead>Status</TableHead><TableHead /></TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((c) => (
                <TableRow key={c.id} data-testid={`company-row-${c.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{c.code}</TableCell>
                  <TableCell className="flex items-center gap-2 text-foreground"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{c.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{c.gstin || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{c.state || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={c.is_active} disabled={!isOrgAdmin} onCheckedChange={() => toggleActive(c)} data-testid={`company-active-toggle-${c.code}`} />
                      <Badge variant={c.is_active ? "default" : "secondary"}>{c.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    {isOrgAdmin && <DeleteCompanyDialog company={c} onDeleted={() => { load(); refreshCompanyCtx?.(); }} onDeactivate={() => toggleActive(c)} />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateCompanyDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/masters/companies", form);
      toast.success(`Company ${form.code} created`);
      setForm(EMPTY);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-company-button"><Plus className="h-4 w-4" /> New Company</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Company</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-company-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input placeholder="Automatic if blank" data-testid="company-code-input" value={form.code} onChange={set("code")} className="mt-1.5" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Name</Label>
              <Input required data-testid="company-name-input" value={form.name} onChange={set("name")} className="mt-1.5" placeholder="SA Construction" />
              <FieldError error={errors.name} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>GSTIN</Label>
              <Input data-testid="company-gstin-input" value={form.gstin} onChange={set("gstin")} className="mt-1.5" />
            </div>
            <div>
              <Label>State</Label>
              <Input data-testid="company-state-input" value={form.state} onChange={set("state")} className="mt-1.5" placeholder="Rajasthan" />
            </div>
          </div>
          <div>
            <Label>Address</Label>
            <Textarea data-testid="company-address-input" value={form.address} onChange={set("address")} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-company-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteCompanyDialog({ company, onDeleted, onDeactivate }) {
  const [open, setOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setUsage(null);
    try {
      const { data } = await api.get(`/masters/companies/${company.id}/usage`);
      setUsage(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setOpen(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/masters/companies/${company.id}`);
      toast.success(`${company.name} deleted`);
      setOpen(false);
      onDeleted?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="ghost" title="Delete company" onClick={openDialog} data-testid={`delete-company-${company.code}`}>
        <Trash2 className="h-3.5 w-3.5 text-rose-400" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Delete {company.name}?</DialogTitle></DialogHeader>
          {!usage ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking whether this company has any data…</p>
          ) : usage.last_company ? (
            <p className="text-sm text-muted-foreground">This is your only company. Create another company before deleting this one.</p>
          ) : !usage.can_delete ? (
            <div className="space-y-3 text-sm">
              <p className="text-rose-600 dark:text-rose-400">This company can't be deleted because it already has data:</p>
              <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
                {usage.blocking.map((r) => <li key={r.table}>{r.count} {r.label}</li>)}
              </ul>
              <p className="text-muted-foreground">Deleting it would destroy that history. Switch it to <b>Inactive</b> instead — it disappears from selections but its records stay for reports and audit.</p>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <p className="text-foreground">No accounts, projects or documents were ever recorded in this company, so it can be deleted.</p>
              {usage.setup_to_remove.length > 0 && (
                <p className="text-muted-foreground">Its set-up records will be removed too: {usage.setup_to_remove.map((r) => `${r.count} ${r.label}`).join(", ")}.</p>
              )}
              <p className="text-xs text-muted-foreground">This cannot be undone.</p>
            </div>
          )}
          <DialogFooter>
            {usage && !usage.can_delete && !usage.last_company && company.is_active && (
              <Button variant="outline" onClick={() => { setOpen(false); onDeactivate?.(); }}>Make inactive</Button>
            )}
            {usage?.can_delete && (
              <Button variant="destructive" disabled={busy} onClick={remove} data-testid="confirm-delete-company">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete company
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
